import {
  Children,
  forwardRef,
  isValidElement,
  useEffect,
  useId,
  useRef,
  type ChangeEvent,
  type FocusEvent,
  type InputHTMLAttributes,
  type MutableRefObject,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { AlertCircle, Check, Lock, Minus } from 'lucide-react';
import { SearchSelect, type SearchOption } from './SearchSelect';
import { cn } from '@/lib/cn';
import { FORMAT_PLACEHOLDER, houseFormat, type HouseFormat } from '@/lib/houseFormat';

/** Guideline §05 — Form Components. Shared validation states. */
export type FieldState = 'default' | 'error' | 'success' | 'locked';

const CONTROL_BASE =
  'w-full rounded-lg border bg-white text-sm text-body transition-colors placeholder:text-muted/70 ' +
  'focus:outline-none disabled:cursor-not-allowed disabled:bg-cloud disabled:text-muted';

const STATE_RING: Record<FieldState, string> = {
  default: 'border-line focus:border-navy focus:shadow-focus-navy',
  error: 'border-danger focus:border-danger focus:shadow-focus-danger',
  success: 'border-signal focus:border-signal focus:shadow-focus-signal',
  locked: 'border-line bg-cloud text-muted',
};

/** Label + helper/error wrapper. Wire `htmlFor` by passing the control's id. */
export function Field({
  label,
  htmlFor,
  hint,
  error,
  required,
  className,
  children,
  action,
}: {
  label?: ReactNode;
  htmlFor?: string;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  className?: string;
  children: ReactNode;
  /** Rendered on the label row, right-aligned — e.g. a "Forgot password?" link. */
  action?: ReactNode;
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      {(label || action) && (
        <div className="flex items-baseline justify-between gap-3">
          {label && (
            <label htmlFor={htmlFor} className="text-sm font-semibold text-body">
              {label}
              {required && (
                <span aria-hidden className="ml-0.5 text-danger">
                  *
                </span>
              )}
            </label>
          )}
          {action}
        </div>
      )}
      {children}
      {error ? (
        <p className="flex items-center gap-1.5 text-helper text-danger-600">
          <AlertCircle aria-hidden className="h-3.5 w-3.5 shrink-0" />
          {error}
        </p>
      ) : (
        hint && <p className="text-helper text-muted">{hint}</p>
      )}
    </div>
  );
}

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  state?: FieldState;
  leadingIcon?: ReactNode;
  /** Rendered inside the field on the right — e.g. a password reveal toggle. */
  trailingSlot?: ReactNode;
  inputSize?: 'md' | 'lg';
  /**
   * Tidy what was typed into the house format as the field is left.
   *
   * The server applies the same rule at the model boundary whether this is set
   * or not, so this changes nothing about what gets stored — it only means the
   * field shows the stored form straight away rather than at the next reload.
   * Applied on blur, never on keystroke: rewriting under somebody mid-word is
   * how a field fights the person using it.
   */
  format?: HouseFormat;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { state = 'default', leadingIcon, trailingSlot, inputSize = 'lg', format, className, disabled, onBlur, placeholder, ...props },
  ref,
) {
  const locked = state === 'locked' || disabled;

  /*
    A field that will rewrite what you typed shows the shape it wants first.

    The example comes from the rule, not from the form, so forty-odd fields
    cannot drift into forty-odd ideas of what good looks like. A caller with a
    better example of its own still wins.
  */
  const example = placeholder ?? (format ? FORMAT_PLACEHOLDER[format] || undefined : undefined);

  const tidy = (event: FocusEvent<HTMLInputElement>) => {
    if (format && event.target.value) {
      const formatted = houseFormat(format, event.target.value);

      if (formatted !== event.target.value) {
        /*
         * Through the native setter, so React sees it.
         *
         * Assigning `.value` on a controlled input is overwritten on the next
         * render; the setter plus a dispatched input event is what makes the
         * owning form's onChange fire and the new value stick.
         */
        const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
        setter?.call(event.target, formatted);
        event.target.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    onBlur?.(event);
  };

  return (
    <div className="relative">
      {leadingIcon && (
        <span
          aria-hidden
          className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted [&>svg]:h-[18px] [&>svg]:w-[18px]"
        >
          {leadingIcon}
        </span>
      )}
      <input
        ref={ref}
        disabled={locked}
        aria-invalid={state === 'error' || undefined}
        placeholder={example}
        onBlur={tidy}
        className={cn(
          CONTROL_BASE,
          STATE_RING[state],
          inputSize === 'lg' ? 'h-12' : 'h-10',
          leadingIcon ? 'pl-11' : 'pl-3.5',
          trailingSlot ? 'pr-11' : 'pr-3.5',
          className,
        )}
        {...props}
      />
      {trailingSlot && (
        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted">{trailingSlot}</span>
      )}
      {state === 'success' && !trailingSlot && (
        <Check aria-hidden className="absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-signal" />
      )}
      {state === 'locked' && !trailingSlot && (
        <Lock aria-hidden className="absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
      )}
    </div>
  );
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & { state?: FieldState }>(
  function Textarea({ state = 'default', className, rows = 4, ...props }, ref) {
    return (
      <textarea
        ref={ref}
        rows={rows}
        aria-invalid={state === 'error' || undefined}
        className={cn(CONTROL_BASE, STATE_RING[state], 'resize-y px-3.5 py-2.5', className)}
        {...props}
      />
    );
  },
);

/**
 * How many options before a list is typed at rather than scrolled.
 *
 * Under this, a native select is the better control: it is one tap on a phone
 * and needs no JavaScript to work. Over it — a hundred and forty-six workers,
 * every project, every certification type — scrolling stops being a choice, so
 * the same `<Select>` becomes a type-ahead without the screen asking for it
 * (client round 7b: "anywhere in the page or modals").
 */
const TYPE_AHEAD_FROM = 8;

/** The options inside a `<Select>`, flattened out of any `<optgroup>`. */
function readOptions(children: ReactNode, group: string | null = null): SearchOption[] {
  return Children.toArray(children).flatMap((child) => {
    if (!isValidElement(child)) return [];

    if (child.type === 'optgroup') {
      const props = child.props as { label?: string; children?: ReactNode };

      return readOptions(props.children, props.label ?? null);
    }

    if (child.type === 'option') {
      const props = child.props as { value?: string | number; children?: ReactNode; disabled?: boolean };
      if (props.disabled) return [];

      const label = Children.toArray(props.children)
        .map((part) => (typeof part === 'string' || typeof part === 'number' ? String(part) : ''))
        .join('')
        .trim();

      return [{ value: String(props.value ?? label), label: label || String(props.value ?? ''), group }];
    }

    return [];
  });
}

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement> & { state?: FieldState }>(
  function Select({ state = 'default', className, children, ...props }, ref) {
    const options = readOptions(children);

    /*
      A long list is typed at. The placeholder option — the empty-valued
      "Select a project…" every form starts with — becomes the placeholder
      rather than a row, because an empty row in a search list reads as a bug.
    */
    if (options.length >= TYPE_AHEAD_FROM && !props.multiple) {
      const placeholder = options.find((option) => option.value === '');
      const { onChange, value, disabled, id, ...rest } = props;

      return (
        <SearchSelect
          id={id}
          className={className}
          aria-label={rest['aria-label']}
          disabled={disabled}
          value={value === undefined || value === null ? '' : String(value)}
          placeholder={placeholder?.label ?? 'Search…'}
          options={options.filter((option) => option.value !== '')}
          onChange={(next) =>
            // The call sites read `event.target.value`, so that is what they get.
            onChange?.({ target: { value: next } } as ChangeEvent<HTMLSelectElement>)
          }
        />
      );
    }

    return (
      <select
        ref={ref}
        className={cn(
          CONTROL_BASE,
          STATE_RING[state],
          'h-12 cursor-pointer appearance-none bg-[length:16px] bg-[right_0.875rem_center] bg-no-repeat px-3.5 pr-10',
          className,
        )}
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236C757E' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")",
        }}
        {...props}
      >
        {children}
      </select>
    );
  },
);

export interface CheckboxProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode;
  /** Partial selection — drives the native `indeterminate` DOM property. */
  indeterminate?: boolean;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, id, className, indeterminate = false, ...props },
  forwardedRef,
) {
  const generated = useId();
  const inputId = id ?? generated;
  const innerRef = useRef<HTMLInputElement>(null);

  // `indeterminate` has no HTML attribute; it must be set on the DOM node.
  useEffect(() => {
    if (innerRef.current) innerRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <div className={cn('flex items-center gap-2.5', className)}>
      <span className="relative inline-flex shrink-0">
        <input
          ref={(node) => {
            // Keep a private handle for the indeterminate effect while still
            // honouring whatever ref the caller passed in.
            (innerRef as MutableRefObject<HTMLInputElement | null>).current = node;
            if (typeof forwardedRef === 'function') forwardedRef(node);
            else if (forwardedRef)
              (forwardedRef as MutableRefObject<HTMLInputElement | null>).current = node;
          }}
          id={inputId}
          type="checkbox"
          className={cn(
            'peer h-[18px] w-[18px] cursor-pointer appearance-none rounded-[4px] border border-line bg-white transition-colors',
            'checked:border-navy checked:bg-navy indeterminate:border-navy indeterminate:bg-navy',
            'focus-visible:shadow-focus-navy disabled:cursor-not-allowed disabled:bg-cloud',
          )}
          {...props}
        />
        {/* Tick is a sibling overlay so the control keeps a native checkbox's semantics. */}
        {indeterminate ? (
          <Minus
            aria-hidden
            className="pointer-events-none absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 stroke-[3.5] text-white"
          />
        ) : (
          <Check
            aria-hidden
            className="pointer-events-none absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 stroke-[3.5] text-white opacity-0 transition-opacity peer-checked:opacity-100"
          />
        )}
      </span>
      {label && (
        <label htmlFor={inputId} className="cursor-pointer select-none text-sm text-body">
          {label}
        </label>
      )}
    </div>
  );
});

export function Radio({
  label,
  id,
  className,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label?: ReactNode }) {
  const generated = useId();
  const inputId = id ?? generated;
  return (
    <div className={cn('flex items-center gap-2.5', className)}>
      <input
        id={inputId}
        type="radio"
        className={cn(
          'h-[18px] w-[18px] shrink-0 cursor-pointer appearance-none rounded-full border border-line bg-white transition-colors',
          'checked:border-[5px] checked:border-navy',
          'focus-visible:shadow-focus-navy disabled:cursor-not-allowed disabled:bg-cloud',
        )}
        {...props}
      />
      {label && (
        <label htmlFor={inputId} className="cursor-pointer select-none text-sm text-body">
          {label}
        </label>
      )}
    </div>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
  disabled,
  className,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label?: ReactNode;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <div className={cn('flex items-center gap-3', className)}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative h-6 w-11 shrink-0 rounded-full transition-colors focus-visible:shadow-focus-navy disabled:cursor-not-allowed disabled:opacity-50',
          checked ? 'bg-signal' : 'bg-line',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform',
            checked ? 'translate-x-[22px]' : 'translate-x-0.5',
          )}
        />
      </button>
      {label && <span className="select-none text-sm text-body">{label}</span>}
    </div>
  );
}
